import { getCatalog, getSingleMovie } from '../../../api/movies/index';
import VODRow from '../components/VODRow';
import VODComponent from '../components/VODComponent';
import { checkImage } from '../../../utils/helpers';
import { getSingleSeries, getSingleSeriesInfo } from '../../../api/series';
import { getUsersProfile } from '../../../api/users';
import { store } from '../../../utils/globalData';

const mapMovie = (movies, id, rowIndex, index) => {
  const movie = Object.values(movies).find((movie) => movie.id === id);

  if (!movie) {
    console.error(`Movie with id ${id} not found`);
    return null;
  }
  // console.log(movie, 'Movie');

  return {
    collision: true,
    type: VODComponent,
    src: checkImage(movie?.alternative_covers, movie['cover_art.expiring_url'] ?? ''),
    isLocked: movie.tvod ?? false,
    isProtected: movie?.locked ?? false,
    rowIndex, // Dodali smo rowIndex
    index, // Dodali smo index
    stream: movie?.hls_stream_url ?? '',
    w: 204,
    h: 290,
    data: {
      originalImage: movie['cover_art.expiring_url'] ?? 'default_image.png',
      kind: movie.kind ?? 'Unknown',
      id: movie.id,
      title: movie.name ?? 'No Title',
      description: movie.description ?? 'No Description Available',
      cover: movie['screenshot.expiring_url'] ?? 'default_cover.png',
      category: movie.tags ?? [],
      duration: movie.duration ?? '0',
      rating: movie.imdb?.rating?.avg ?? '−',
      votes: movie.imdb?.rating?.num_votes ?? '−',
      contentRating: movie.content_rating ?? 'Not Rated',
    },
  };
};

const mapSeries = (series, id, rowIndex, index) => {
  const serie = Object.values(series).find((serie) => serie.id === id);
  if (!serie) {
    console.error(`Series with id ${id} not found`);
    return null;
  }
  return {
    collision: true,
    type: VODComponent,
    src: checkImage(serie.alternative_covers, serie['cover_art.expiring_url']),
    isProtected: false,
    w: 204,
    h: 290,
    rowIndex, // Dodali smo rowIndex
    index, // Dodali smo index
    data: {
      originalImage: serie['cover_art.expiring_url'],
      kind: serie.kind,
      id: serie.id,
      title: serie.name ?? '',
      description: serie.description ?? '',
      cover: serie['screenshot.expiring_url'],
      category: serie.tags ?? '',
      duration: serie.duration ?? '',
      rating: serie.imdb.rating === null ? '−' : serie.imdb.rating.avg,
      votes: serie.imdb.rating === null ? '−' : serie.imdb.rating.num_votes,
      contentRating: serie.content_rating,
    },
  };
};

const mapCollections = (collections, id, rowIndex, index) => {
  const collection = Object.values(collections).find((collection) => collection.id === id);
  return {
    collision: true,
    type: VODComponent,
    src: collection['screenshot.expiring_url'] ?? '',
    isProtected: false,
    isLocked: false,
    w: 290,
    h: 204,
    rowIndex, // Dodali smo rowIndex
    index, // Dodali smo index
    data: {
      kind: 'collection',
      id: collection?.id,
      title: collection?.name ?? '',
      description: '',
      cover: collection['screenshot.expiring_url'] ?? '',
      category: '',
      duration: '',
      rating: 1,
      votes: 0,
      contentRating: 0,
    },
  };
};

export default async function (page) {
  let data = store.getData();

  if (!data) {
    console.log('Nema u store, preuzimam podatke...');
    const catalogData = await getCatalog();
    data = catalogData.data;
    store.setData(data);
    store.setMyListFlag(true);
  } else {
    console.log('Podaci su već u store');
  }
  const rows = data.data.root_index;
  const categories = data.index;
  let firstItem;

  const pageItems = rows
    .filter((row) => {
      const category = Object.values(categories[row.kind]).find(
        (category) => category.id === row.id
      );
      return category && !(category.name === 'Continue Watching' && category.items.length === 0);
    })
    .map((row, rowIndex) => {
      const category = Object.values(categories[row.kind]).find(
        (category) => category.id === row.id
      );

      const isCollectionRow = !category.items.some((item) => item.kind !== 'collection');
      const title = category?.name ?? '';
      const items = category?.items
        .map((item, index) => {
          switch (item.kind) {
            case 'movie':
              return mapMovie(categories.movies, item.id, rowIndex, index);
            case 'subseries':
              return mapSeries(categories.subseries, item.id, rowIndex, index);
            case 'collection':
              return mapCollections(categories.collections, item.id, rowIndex, index);
            default:
              return null;
          }
        })
        .filter((pageItem) => Boolean(pageItem));

      if (!isCollectionRow && !firstItem) {
        firstItem = category.items[0];
      }
      return {
        collision: true,
        type: VODRow,
        scrollIndex: 2,
        title,
        h: isCollectionRow ? 319 : 400,
        items: items.map((item, index) => ({ ...item, index: index })),
        style: { itemSpacing: isCollectionRow ? 30 : 40 },
      };
    });

  // Getting profile filter
  const { data: users } = await getUsersProfile();
  const filter = users?.user?.approvals || [];

  let filteredPageItems = pageItems.filter((item) => item !== null);
  // Postavite hero element ako je potrebno
  try {
    if (filteredPageItems[1]?.items[0]?.data?.kind === 'movie') {
      const itemData = filteredPageItems[1].items[0].data || {};

      page._heroId = {
        data: {
          isProtected: filteredPageItems?.[1]?.items?.[0]?.isProtected ?? false,
          title: itemData?.title ?? '',
          description: itemData?.description ?? '',
          cover: itemData?.cover ?? 'default-cover.jpg',
          kind: itemData?.kind ?? '',
          id: itemData?.id ?? '',
          category: itemData?.category ?? '',
          duration: itemData?.duration ?? '',
          rating: itemData?.rating ?? '',
          votes: itemData?.votes ?? '',
          contentRating: itemData?.contentRating ?? '',
          tvod: filteredPageItems?.[1]?.items?.[0]?.isProtected ?? false,
          languages: itemData?.stream ?? '',
        },
      };
    }

    if (filteredPageItems[1]?.items[0]?.data?.kind === 'subseries') {
      const data = await getSingleSeries(filteredPageItems[1].items[0].data.id);
      const seriesEntry = data?.data || {};

      let serieId = data?.data?.last_watched_movie_id || data?.data?.[0]?.movies?.[0]?.id;

      if (!serieId) {
        throw new Error('Invalid series data: No movie ID found.');
      }

      const seriesInfoo = await getSingleSeriesInfo(serieId);
      const seriesInfo = seriesInfoo?.data?.movie || {};

      page._heroId = {
        data: {
          isProtected: seriesInfo?.locked ?? true,
          title: seriesEntry?.name ?? '',
          description: seriesEntry?.description ?? '',
          cover: filteredPageItems?.[1]?.items?.[0]?.data?.cover ?? 'default-cover.jpg',
          kind: filteredPageItems?.[1]?.items?.[0]?.data?.kind ?? 'subseries',
          id: seriesInfo?.id ?? '',
          category: seriesInfo?.tags ?? '',
          duration: filteredPageItems?.[1]?.items?.[0]?.data?.duration ?? '',
          rating: filteredPageItems?.[1]?.items?.[0]?.data?.rating ?? '',
          votes: filteredPageItems?.[1]?.items?.[0]?.data?.votes ?? '',
          contentRating: filteredPageItems?.[1]?.items?.[0]?.data?.contentRating ?? '',
          tvod: seriesInfo?.tvod ?? false,
          languages: seriesInfo?.languages ?? [],
          idEntrySerie: seriesEntry?.id ?? '',
        },
      };
    }
  } catch (error) {
    console.error('Error fetching hero data:', error.message, { filteredPageItems });
    page._heroId = undefined;
  }

  page.filter = filter;
  page.secondRow = filteredPageItems;
  page._Column.items = filteredPageItems;
}
